import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { CircularProgress } from '@mui/material';
import { DocumentsSVG } from '@/components/Icon/Documents';
import * as S from '@/components/DocumentSharing/styles';
import {
  acceptDocumentShareInvitation, resolveDocumentShareInvitation, resolveExternalShareInvitation, invitationErrorKey,
  ExternalInvitationDetails, InvitationDetails, InvitationErrorKey,
} from '@/api/documentShareInvitation.api';
import { invitationTokenFromHash, pendingDocumentInvitation, rememberDocumentInvitation, clearDocumentInvitation } from '@/utils/documentShareInvitation';
import ExternalInvitation from './ExternalInvitation';

export function PendingDocumentInvitationNotice() {
  const { isLogin } = useAppSelector(getAuthSelector);
  const location = useLocation();
  // @ts-ignore 翻譯鍵型別超過 CRA 檢查器的遞迴深度，沿用相鄰分享元件的局部處理。
  const { t } = useTranslation();
  if (!isLogin || location.pathname === '/share-invitation' || !pendingDocumentInvitation()) return null;
  return <S.ResumeNotice role="status"><Link to="/share-invitation">{t('documentEmailShare.continueInvitation')}</Link></S.ResumeNotice>;
}

export default function DocumentShareInvitationPage() {
  // @ts-ignore 翻譯鍵型別超過 CRA 檢查器的遞迴深度，沿用相鄰分享元件的局部處理。
  const { t, i18n } = useTranslation();
  const { isLogin, userData } = useAppSelector(getAuthSelector);
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => location.hash ? invitationTokenFromHash(location.hash) : pendingDocumentInvitation());
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<InvitationErrorKey | ''>('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  // External (account-free) invitations are checked first; a 404 means a member invitation or an invalid link.
  const [externalDetails, setExternalDetails] = useState<ExternalInvitationDetails | null>(null);
  const [externalChecked, setExternalChecked] = useState(false);
  useEffect(() => {
    if (!token) return;
    let active = true;
    setError(''); setDetails(null); setLoading(false);
    setExternalChecked(false); setExternalDetails(null);
    resolveExternalShareInvitation(token).then(({ data }) => {
      if (active) setExternalDetails(data.data);
    }).catch(e => {
      if (!active) return;
      if ((e as { response?: { status?: number } })?.response?.status === 410) {
        setError('unavailable'); clearDocumentInvitation();
      }
    }).finally(() => { if (active) setExternalChecked(true); });
    return () => { active = false; };
  }, [token]);
  useEffect(() => {
    const incoming = invitationTokenFromHash(location.hash);
    if (incoming) {
      setToken(incoming);
      if (rememberDocumentInvitation(incoming)) navigate('/share-invitation', { replace: true });
    } else if (location.hash) {
      setToken(null); clearDocumentInvitation();
      navigate('/share-invitation', { replace: true });
    }
  }, [location.hash, navigate]);
  useEffect(() => {
    if (!isLogin || !token || !externalChecked || externalDetails || error === 'unavailable') return;
    let active = true;
    setError(''); setDetails(null); setLoading(true);
    resolveDocumentShareInvitation(token).then(({ data }) => {
      if (active) {
        setDetails(data.data);
        if (data.data.status !== 'pending') clearDocumentInvitation();
      }
    }).catch(e => {
      if (active) {
        const key = invitationErrorKey(e);
        setError(key);
        if (key === 'unavailable') clearDocumentInvitation();
      }
    })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- error only gates the start, re-running on it would loop.
  }, [isLogin, userData?.id, token, externalChecked, externalDetails]);
  const accept = async () => {
    if (!token || busy) return;
    setBusy(true); setError('');
    try {
      const { data } = await acceptDocumentShareInvitation(token);
      setDetails(data.data); clearDocumentInvitation();
    } catch (e) { setError(invitationErrorKey(e)); }
    finally { setBusy(false); }
  };
  const accepted = details?.status === 'accepted';
  const dateFormat = new Intl.DateTimeFormat(i18n?.language === 'en' ? 'en-GB' : 'zh-Hant', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  if (token && externalDetails) {
    return <S.InvitationPage>
      <S.InvitationPanel aria-labelledby="share-invitation-heading">
        <S.Heading as="h1" id="share-invitation-heading">{t('documentEmailShare.externalTitle')}</S.Heading>
        <ExternalInvitation token={token} details={externalDetails} dateFormat={dateFormat} />
      </S.InvitationPanel>
    </S.InvitationPage>;
  }
  return <S.InvitationPage>
    <S.InvitationPanel aria-labelledby="share-invitation-heading">
      <S.Heading as="h1" id="share-invitation-heading">{t(accepted ? 'documentEmailShare.acceptedTitle' : 'documentEmailShare.reviewTitle')}</S.Heading>
      {token && !externalChecked ? <S.EmptyState role="status" aria-live="polite">{t('documentEmailShare.loading')}</S.EmptyState>
        : !token || (error === 'unavailable' && !isLogin) ? <S.Feedback $error role="alert">{t('documentEmailShare.errors.unavailable')}</S.Feedback> : !isLogin ? <>
        <S.Description>{t('documentEmailShare.signInDescription')}</S.Description>
        <S.PageActions>
          <S.ActionLink to="/auth/signin?path=%2Fshare-invitation">{t('documentEmailShare.signIn')}</S.ActionLink>
          <S.ActionLink $secondary to="/auth/signup">{t('documentEmailShare.register')}</S.ActionLink>
        </S.PageActions>
        <S.Hint>{t('documentEmailShare.reopenAfterRegistration')}</S.Hint>
      </> : <>
        {loading && <S.EmptyState role="status" aria-live="polite">{t('documentEmailShare.loading')}</S.EmptyState>}
        {error && <S.Feedback $error role="alert">{t(`documentEmailShare.errors.${error}`)}</S.Feedback>}
        {details && <>
          <S.Description>{t(accepted ? 'documentEmailShare.acceptedDescription' : 'documentEmailShare.acceptDescription')}</S.Description>
          <S.Target style={{ margin: '28px 0 0' }}>
            <S.FileIcon aria-hidden="true"><DocumentsSVG /></S.FileIcon>
            <div style={{ minWidth: 0 }}><small>{t(`documentEmailShare.target.${details.target_type}`)}</small><strong>{details.target_name || t('documentEmailShare.sharedItem')}</strong></div>
          </S.Target>
          <S.DetailList>
            <div><dt>{t('documentEmailShare.invitationStatus')}</dt><dd><S.Status $status={details.status}>{t(`documentEmailShare.status.${details.status}`)}</S.Status></dd></div>
            <div><dt>{t('documentEmailShare.permission')}</dt><dd>{t('documentEmailShare.readDownload')}</dd></div>
            {!accepted && <div><dt>{t('documentEmailShare.expires')}</dt><dd><time dateTime={details.expires_at}>{dateFormat.format(new Date(details.expires_at))}</time></dd></div>}
          </S.DetailList>
          <S.PageActions>
            {details.status === 'pending' && error !== 'unavailable' && <S.PrimaryButton type="button" disabled={busy} aria-busy={busy} onClick={() => void accept()}>
              {busy && <CircularProgress size={18} aria-hidden="true" />}{t('documentEmailShare.accept')}
            </S.PrimaryButton>}
            {accepted && <S.ActionLink to="/sharedDocuments">{t('documentEmailShare.openShared')}</S.ActionLink>}
          </S.PageActions>
          <S.Hint>{t('documentEmailShare.readOnlyNote')}</S.Hint>
        </>}
      </>}
    </S.InvitationPanel>
  </S.InvitationPage>;
}
